function noop() { }

function fallback(predicate, fallback) {
  predicate = isFunction(predicate) ? predicate : identity;

  return function (value) {
    return predicate(value) ? value : fallback;
  }
}

/**
 * Returns a new function that will return `true` when called with a set of
 * arguments iff all functions passed to this higher-order function return
 * `true` when called with the same set of arguments, otherwise, will return
 * `false`.
 *
 * @param {...*} fns One or more supposed test functions.
 * @return {function} Function that will always return a boolean.
 */
function trueForEvery(...fns) {
  // Flatten the functions passed as arguments into a flat array.
  // Replace the non-functions with a function that will constantly return
  // the value as is.
  fns = [].concat(fns).map(function mapper_(fn) {
    return isFunction(fn) ? fn : constant(fn);
  });

  return function (...args) {
    // Keep calling each function in the `fns` list with `args` until one
    // fails to return `true` when called. If that happens, stop and return
    // `false`. Otherwise, return `true`.
    return fns.every(function call_(fn) {
      return fn(...args) === true;
    });
  };
}

/**
 * Returns with the value of the first argument it is called with. The
 * value is returned as is (unchanged), hence the name identity.
 *
 * @param {*} value The value (constant).
 * @return {function} Function that will always return `value`.
 */
function identity(value) {
  return value;
}

/**
 * Returns a new function that is guaranteed to always return the value
 * specified whenever it is called, regardless of the arguments it will
 * be called with. This is a higher-order function.
 *
 * @param {*} value The value (constant).
 * @return {function} Function that will always return `value`.
 */
function constant(value) {
  return function constantFn_() {
    return value;
  };
}

/**
 * Checks if the given value is a string.
 *
 * @param {*} value The value to check.
 * @return {boolean} True if `value` is a string, and false otherwise.
 */
function isString(value) {
  return typeof value === "string";
}

/**
 * Checks if the given value is a boolean.
 *
 * @param {*} value The value to check.
 * @return {boolean} True if `value` is a boolean, and false otherwise.
 */
function isBoolean(value) {
  return typeof value === "boolean";
}

/**
 * Checks if the given value is callable.
 *
 * @param {*} value The value to check.
 * @return {boolean} True if `value` is callable, and false otherwise.
 */
function isFunction(value) {
  return typeof value === "function";
}

/**
 * Checks if the given value is a plain JavaScript object. The check
 * isn't foolproof, but will be fairly reliable for most objects.
 *
 * @param {*} value The value to check.
 * @return {boolean} True if `value` is a plain JS object, and false
 *                   otherwise.
 */
function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

/**
 * Defines one or more enumerable properties on a specified object.
 * The target object is passed as first argument, while the properties
 * to define on the target object can be optionally passed as second
 * argument (as an object of property-value pairs) to the function.
 *
 * The defined properties on the object will neither be configurable
 * nor writable, even though they are enumerable. If a property with
 * the same name occurs multiple times in the properties object, the
 * last value associated with that property will be used.
 *
 * This function can potentially throw errors resulting from a call to
 * `Object.defineProperties()`.
 *
 * @param {object} obj The original object.
 * @param {*} properties Supposed object of property-value pairs.
 * @return {object} Same as `obj`, but with the enumerable properties.
 * @throws {TypeError|Error}
 */
function withProperties(obj, properties, writable = false) {
  return Object.defineProperties(
    obj,
    isPlainObject(properties)
      ? Object.keys(properties).reduce((props, prop) => {
        props[prop] = {
          enumerable: true,
          writable: writable === true,
          value: properties[prop]
        };

        return props;
      }, {})
      : {}
  );
}

function withAccessors(property, accessors) {
  const { getter, setter, extendable = arguments[2] } =
    isFunction(accessors)
      ? (accessors.length ? { setter: accessors } : { getter: accessors })
      : (isPlainObject(accessors) ? accessors : {});

  Object.defineProperty(this, property, {
    enumerable: true,
    configurable: extendable === true,
    get: isFunction(getter) ? getter.bind(this) : noop,
    set: isFunction(setter) ? setter.bind(this) : noop
  });
}

/**
 * Normalizes the specified value (if a string) to snake case. It
 * removes all non-alphanumeric characters and separates with
 * underscores. For a non-string value, it returns an empty string.
 *
 * @param {*} value Supposed string to be normalized to snake case.
 * @return {string} The normalized string or an empty string.
 */
function snakeCase(value) {
  if (isString(value) && (value = value.toLowerCase().trim())) {
    return (
      value
        // replace unwanted characters
        .replace(/[^a-z0-9_]+/g, "_")
        // strip off excess underscores
        .replace(/^_+|_+(?=_)|_+$/g, "")
    );
  }

  return String();
}

function call(fn) {
  return isFunction(fn) ? Function.prototype.call.bind(fn) : fn;
}

function callback(fn, fallbackFn) {
  return isFunction(fn) ? fn : isFunction(fallbackFn) ? fallbackFn : noop;
}

function matchedString(regex, value) {
  return (regex instanceof RegExp && value && regex.test(String(value).trim()))
    ? value.trim() || null
    : null;
}

function resolveBoolean(value, defaultBool = true) {
  if ([true, 'true', 1, '1'].indexOf(value) >= 0) return true;
  if ([false, 'false', 0, '0'].indexOf(value) >= 0) return false;
  return resolveBoolean(defaultBool) === true;
}

function getValueAtPath(object, path) {
  // Ensure that path is a string, default to an empty string if not provided.
  // Replace bracket notation occurrences on path with dot notation.
  // Split path (to array) using the `.` delimeter, discard empty elements.
  path = (path ? String(path) : String())
    .replace(/\[((?:['"])?)([^[\]]*)\1\]/g, function __replacer__($, $$, key) {
      return `.${Number(key) || String(key)}`;
    })
    .split(/\.+/)
    .filter(Boolean);

  // Value is originally the root object.
  let value = object;

  // For every key in the path array, try to access the value at that address.
  // Update `value` with the value at that address.
  // Break the loop if `value` is `undefined`.
  for (const key of path) {
    // If value is an object instance or plain JS object and has the given key,
    // access the given key on the object. Otherwise, if it is an array, try to
    // access the numeric key(index) on the array. Otherwise, it is `undefined`.
    try {
      value = (value instanceof Object || isPlainObject(value)) && key in value
        ? value[key]
        : value[ value instanceof Array ? Number(key) : key ];

      if (value === undefined) break;
    } catch (e) {
      value = undefined;
      break;
    }
  }

  return value;
}

function objectWithAccessors(accessorsObject, freeze = true) {
  const newObject = this || Object.create(null);

  if (isPlainObject(accessorsObject)) {
    const _withAccessors = withAccessors.bind(newObject);

    Object.keys(accessorsObject).forEach(key => {
      _withAccessors(key, accessorsObject[key]);
    });
  }

  return freeze === false || newObject === this
    ? newObject
    : Object.freeze(newObject);
}

function objectWithAccessorsList(accessorsList, freeze = true) {
  objectWithAccessorsList = function __objectWithAccessorsList__(accessorsList, freeze = true) {
    const withChangeSubscriptions = isPlainObject(accessorsList);

    return objectWithAccessors.call(this, (
      [].concat(withChangeSubscriptions ? Object.keys(accessorsList) : accessorsList)
        .reduce((accessorsObj, key) => {
          if (isString(key) || typeof key === 'symbol') {
            accessorsObj[key] = withChangeSubscriptions
              ? __createAccessorsPair__.call(accessorsList, key)
              : __createAccessorsPair__();
          }

          return accessorsObj;
        }, {})
    ), freeze);
  }

  function __createAccessorsPair__(key) {
    let currentValue;
    const $this = this;

    function getter() { return currentValue }
    function setter(value) { currentValue = value }

    if (arguments.length && (isString(key) || typeof key === 'symbol') && isPlainObject($this)) {
      setter = function setter(value) {
        if (currentValue !== value) {
          $this[key] instanceof Array &&
            $this[key].forEach(function __triggerChangeCallback__(fn) {
              isFunction(fn) && fn(value, currentValue);
            });

          currentValue = value;
        }
      }
    }

    return { getter, setter };
  }

  return objectWithAccessorsList(accessorsList, freeze);
}

function toArray(val) {
  return [].concat(val).filter(Boolean);
}

function appendToArray(arr, val) {
  return (arr = toArray(arr)).concat(
    arr.indexOf(val) < 0 ? val : []
  );
}

function removeFromArray(arr, val) {
  let index = (arr = toArray(arr)).indexOf(val);
  index = ~index ? index : arr.length;
  return arr.slice(0, index).concat(arr.slice(index + 1));
}

function convertObjectToQueryString(obj) {
  const str = [];
  for (const p in obj) {
      if (obj[p] === undefined) {
          continue;
      }
      /* eslint-disable */
      if (obj.hasOwnProperty(p)) {
          str.push((p) + "=" + (obj[p]));
      }
  }
  return str.join("&");
}

function createQueryString(url = "", queryParams = {}) {
  let queryString = url;
  if (queryParams && Object.keys(queryParams).length > 0) {
      queryString += "?" + convertObjectToQueryString(queryParams);
  }
  return queryString;
}

function optimizeMerchantLogo(url, width = 30, height = 30) {
  try {
    const urlObject = new URL(url);
    const urlParams = Object.fromEntries(new URLSearchParams(urlObject.search))
    const params = {
        ...urlParams,
        width: parseFloat(width),
        height: parseFloat(height),
        resize: 1,
        fit: "crop",
        align: "center",
    }
    return createQueryString(urlObject.origin + urlObject.pathname, params)
  } catch {
    return url;
  }
}

export {
  appendToArray,
  call,
  callback,
  constant,
  getValueAtPath,
  identity,
  isBoolean,
  isFunction,
  isPlainObject,
  isString,
  matchedString,
  noop,
  objectWithAccessors,
  objectWithAccessorsList,
  removeFromArray,
  resolveBoolean,
  snakeCase,
  toArray,
  trueForEvery,
  withAccessors,
  withProperties,
  optimizeMerchantLogo
}
